'use client';

import { useCallback } from 'react';
import { ActionButtonWithDescription } from '@/shared/components/action-button-with-description';
import { ExternalPlayIcon } from '@/shared/components/primitives/svg/external-play-icon';
import { useTranslations } from 'next-intl';
import { cn } from '@ecom/helper-react-class-combine';
import { GameData } from '@/features/games/shared/types/game-data';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { useGameStore } from '@/features/games/shared/store/game-store';
import { isAlreadyRented } from '@/features/games/shared/helpers/is-already-rented';
import { isAlreadyPurchased } from '@/features/games/shared/helpers/is-already-purchased';
import { GameCategories } from '@/shared/constants/game-categories';
import Modal from '@/shared/components/overlay/modal';
import { PlayGameOverlay } from '@/features/games/internal/components/game-detail/play-game-overlay';
import { useHasTrialsLeft } from '@/features/games/internal/hooks/use-has-trials-left';
import { Loader } from '@/shared/components/primitives/loader';
import { openAuthGate } from '@/features/auth/shared/state/auth-gate.store';
import {
  isDownloadProductType,
  needsGameAuthGate,
} from '@/features/games/shared/helpers/needs-game-auth-gate';
import {
  PLAY_GAME_MODAL_KEY,
  startGameDownload,
} from '@/features/games/shared/helpers/start-game-download';

export const PlayGameButton = ({
  gameData,
  className,
}: {
  gameData: GameData;
  className?: string;
}) => {
  const t = useTranslations('GamePage');
  const { loading, authenticatedUser } = useUser();

  const purchaseHistory = useGameStore((s) => s.purchaseHistory);
  const purchaseHistoryLoaded = useGameStore((s) => s.historyLoaded);

  // While we don’t even know auth/purchase-history readiness, show a loading button
  const preloading = loading || (authenticatedUser && !purchaseHistoryLoaded) || false;
  const isMember = authenticatedUser?.isWildMember || false;
  const userOwnsGame =
    isAlreadyPurchased(gameData.sku, purchaseHistory) ||
    isAlreadyRented(gameData.sku, purchaseHistory);
  const gameIsPartOfUnlimitedPlay =
    gameData.categories?.some((c) => c.name === GameCategories.UNLIMITED_PLAY) ?? false;
  const freeToPlay =
    gameData.categories?.some((c) => c.name === GameCategories.FREE_TO_PLAY) ?? false;
  const wildMemberPlay = isMember && gameIsPartOfUnlimitedPlay && !userOwnsGame;

  const { trialsLeft, loading: trialsLoading } = useHasTrialsLeft(gameData.sku);
  const needsTrialsCheck = !freeToPlay && !userOwnsGame && !wildMemberPlay;
  const canPlay = freeToPlay || wildMemberPlay || userOwnsGame || trialsLeft;
  const disabled = loading || (needsTrialsCheck && trialsLoading) || !canPlay;
  const isTrialDownload = !wildMemberPlay && !userOwnsGame && trialsLeft;

  let description;
  let ctaName;
  if (wildMemberPlay) {
    description = t('gameIncludedWithMembership');
    ctaName = 'play_game_membership';
  } else if (userOwnsGame) {
    description = t('downloadFullVersion');
    ctaName = 'play_game_owned';
  } else if (trialsLeft) {
    description = t('downloadFullVersionWithFreeTrial');
    ctaName = 'play_game_trial';
  } else {
    description = t('trailExpiredPurchaseFullVersion');
    ctaName = 'play_game_trial_expired';
  }

  const title =
    gameData.productType === 'online' ? (
      <span className='flex items-center gap-1'>
        <ExternalPlayIcon /> {t('play')}
      </span>
    ) : (
      t('play')
    );

  // Online types are neither gated nor reported as trial downloads.
  const isTrialDownloadProduct = isTrialDownload && isDownloadProductType(gameData.productType);

  const startDownload = useCallback((): void => {
    startGameDownload(gameData.sku, isTrialDownloadProduct);
  }, [gameData.sku, isTrialDownloadProduct]);

  const needsAuthGate = needsGameAuthGate({
    isTrialDownload,
    productType: gameData.productType,
    sessionLoading: loading,
    isAuthenticated: Boolean(authenticatedUser),
  });

  const handleOpenLink = async (): Promise<void> => {
    if (needsAuthGate) {
      openAuthGate({
        view: 'register',
        pendingAction: startDownload,
        resumeAfterRedirect: {
          type: 'trialDownload',
          sku: gameData.sku,
          isDownload: isTrialDownloadProduct,
        },
        trigger: 'trial_download',
      });
      return;
    }

    startDownload();
  };

  return (
    <div>
      <ActionButtonWithDescription
        title={<span className='text-lg'>{title}</span>}
        description={!preloading ? description : <Loader color='primary' size={24} />}
        variant='primary'
        isLoading={preloading}
        onClick={handleOpenLink}
        disabled={disabled}
        ctaName={ctaName}
        className={cn('min-h-20', className)}
      />
      <Modal modalKey={PLAY_GAME_MODAL_KEY}>
        <PlayGameOverlay />
      </Modal>
    </div>
  );
};
